import React from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  VStack,
  HStack,
  Box,
  Text,
  Divider,
  Icon,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import { FaUserFriends, FaHammer, FaBolt, FaArrowRight } from "react-icons/fa";

interface GetStartedModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHireTalent: () => void;
  onBuildProjectCrew: () => void;
  onRequestApiAccess: () => void;
}

interface GetStartedOption {
  id: string;
  icon: IconType;
  iconBg: string;
  iconBorderColor: string;
  iconColor: string;
  title: string;
  description: string;
  onSelect: () => void;
}

export const GetStartedModal: React.FC<GetStartedModalProps> = ({
  isOpen,
  onClose,
  onHireTalent,
  onBuildProjectCrew,
  onRequestApiAccess,
}) => {
  const options: GetStartedOption[] = [
    {
      id: "hire-talent",
      icon: FaUserFriends,
      iconBg: "flatteredFlamingo.50",
      iconBorderColor: "flatteredFlamingo.200",
      iconColor: "flatteredFlamingo.600",
      title: "Hire talent",
      description:
        "Post jobs and hire pre-vetted workers across 50+ trades. Free to use, with smart screening built in.",
      onSelect: onHireTalent,
    },
    {
      id: "build-project-crew",
      icon: FaHammer,
      iconBg: "flatteredFlamingo.50",
      iconBorderColor: "flatteredFlamingo.200",
      iconColor: "flatteredFlamingo.600",
      title: "Build a project crew",
      description:
        "Need a job done end to end? Tell us your needs, we'll source, vet, and deliver with skilled workers.",
      onSelect: onBuildProjectCrew,
    },
    {
      id: "request-api-access",
      icon: FaBolt,
      iconBg: "yellow.50",
      iconBorderColor: "yellow.200",
      iconColor: "yellow.500",
      title: "Request API access",
      description:
        "Integrate facilities management and maintenance directly into your platform with the LaborHack API.",
      onSelect: onRequestApiAccess,
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent maxW="640px" p={{ base: 2, md: 4 }} borderRadius="0">
        <ModalHeader
          p={{ base: 2, md: 4 }}
          pr={{ base: 10, md: 12 }}
          pb={{ base: 1, md: 2 }}
        >
          <Text
            fontSize={{ base: "xl", md: "2xl" }}
            fontWeight="medium"
            color="gray.900"
          >
            How can we help?
          </Text>
        </ModalHeader>
        <ModalCloseButton borderRadius="full" />
        <ModalBody p={{ base: 2, md: 4 }}>
          <Text fontSize={{ base: "sm", md: "md" }} color="gray.600">
            Choose what you&apos;d like to do, we&apos;ll take you there.
          </Text>
          <Divider my={4} borderColor="gray.200" />
          <VStack spacing={3} align="stretch">
            {options.map((option) => (
              <Box
                key={option.id}
                as="button"
                onClick={option.onSelect}
                textAlign="left"
                width="full"
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="0"
                p={4}
                bg="white"
                transition="all 0.2s ease"
                _hover={{
                  shadow: "md",
                  transform: "translateY(-2px)",
                  borderColor: "flatteredFlamingo.500",
                }}
                _active={{ transform: "translateY(0px)" }}
              >
                <HStack spacing={4} align="center">
                  <Box
                    flexShrink={0}
                    borderRadius="md"
                    p={3}
                    bg={option.iconBg}
                    borderWidth="1px"
                    borderColor={option.iconBorderColor}
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={option.icon} color={option.iconColor} boxSize={5} />
                  </Box>
                  <Box flex={1}>
                    <Text
                      fontSize={{ base: "md", md: "lg" }}
                      fontFamily="AeonikPro"
                      fontWeight="semibold"
                      color="gray.900"
                    >
                      {option.title}
                    </Text>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      {option.description}
                    </Text>
                  </Box>
                  <Icon
                    as={FaArrowRight}
                    color="gray.400"
                    boxSize={4}
                    flexShrink={0}
                  />
                </HStack>
              </Box>
            ))}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GetStartedModal;
